import PropTypes from 'prop-types';
import { BsChevronLeft, BsChevronRight, BsThreeDots } from 'react-icons/bs';

import Button from '../Button/Button';

const Pagination = ({
	currentPage = 1,
	totalPages = 1,
	onPageChange,
	showInfo = true,
	totalItems = 0,
	pageSize = 100,
}) => {
	if (totalPages <= 1) return null;

	const getPageNumbers = () => {
		const delta = 2; // Number of pages to show around current page
		const pages = [];
		const rangeStart = Math.max(2, currentPage - delta);
		const rangeEnd = Math.min(totalPages - 1, currentPage + delta);

		// Always show first page
		if (currentPage === 1) {
			pages.push({ number: 1, active: true });
		} else {
			pages.push({ number: 1, active: false });
		}

		// Show dots if there's a gap
		if (rangeStart > 2) {
			pages.push({ type: 'ellipsis' });
		}

		// Show pages around current page
		for (let i = rangeStart; i <= rangeEnd; i++) {
			if (i !== 1 && i !== totalPages) {
				pages.push({ number: i, active: i === currentPage });
			}
		}

		// Show dots if there's a gap
		if (rangeEnd < totalPages - 1) {
			pages.push({ type: 'ellipsis' });
		}

		// Always show last page (if not the same as first page)
		if (totalPages > 1) {
			pages.push({ number: totalPages, active: currentPage === totalPages });
		}

		return pages;
	};

	const pages = getPageNumbers();
	const startItem = (currentPage - 1) * pageSize + 1;
	const endItem = Math.min(currentPage * pageSize, totalItems);

	return (
		<div className="flex flex-col sm:flex-row justify-between items-center gap-3 py-4">
			{showInfo && (
				<div className="text-sm text-text">
					Zeige {startItem} bis {endItem} von {totalItems} Einträgen
				</div>
			)}

			<div className="flex items-center gap-1">
				{/* Previous button */}
				<Button
					variant="outline"
					size="sm"
					onClick={() => onPageChange(currentPage - 1)}
					disabled={currentPage <= 1}
					className="flex items-center px-2"
				>
					<BsChevronLeft className="w-4 h-4" />
					<span className="sr-only">Vorherige Seite</span>
				</Button>

				{/* Page numbers */}
				{pages.map((page, index) => {
					if (page.type === 'ellipsis') {
						return (
							<span key={`ellipsis-${index}`} className="px-2 py-1">
								<BsThreeDots className="w-4 h-4 text-bgBase-800" />
							</span>
						);
					}

					return (
						<Button
							key={page.number}
							variant={page.active ? 'solid' : 'outline'}
							size="sm"
							onClick={() => onPageChange(page.number)}
							className={`px-3 ${page.active ? 'font-semibold' : ''}`}
						>
							{page.number}
						</Button>
					);
				})}

				{/* Next button */}
				<Button
					variant="outline"
					size="sm"
					onClick={() => onPageChange(currentPage + 1)}
					disabled={currentPage >= totalPages}
					className="flex items-center px-2"
				>
					<BsChevronRight className="w-4 h-4" />
					<span className="sr-only">Nächste Seite</span>
				</Button>
			</div>
		</div>
	);
};

Pagination.propTypes = {
	currentPage: PropTypes.number.isRequired,
	totalPages: PropTypes.number.isRequired,
	onPageChange: PropTypes.func.isRequired,
	showInfo: PropTypes.bool,
	totalItems: PropTypes.number,
	pageSize: PropTypes.number,
};

export default Pagination;
